import React, { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import style from "./index.module.scss";

interface OverflowWrapperProps {
    width: number;
    children: React.ReactNode;
    depth?: number;
    className?: string;
}

export default function OverflowWrapper({
    width,
    children,
    className,
    depth = 1
}: OverflowWrapperProps) {
    const [offset, setOffset] = useState(0);
    const [childList, setChildList] = useState<number[]>([]);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ref.current) return;
        const childList = getChildList(ref.current, depth);

        const newList = [];
        for (let elem of childList) {
            newList.push(elem.offsetWidth);
        }
        setChildList(newList);
    }, [children]);

    const totalWidth = useMemo(() => {
        let width = 0;
        for (let i = 0; i < childList.length; i++) {
            width += childList[i];
        }
        return width;
    }, [childList]);

    const offsetWidth = useMemo(() => {
        let width = 0;
        for (let i = 0; i < offset; i++) {
            width += childList[i];
        }
        return width;
    }, [childList, offset]);

    function getChildList(dom: HTMLElement, depth: number): HTMLElement[] {
        if (!ref.current) return [];
        if (depth === 1)
            return (dom.children as unknown as HTMLElement[]) || [];
        return getChildList(dom.firstChild as HTMLElement, depth - 1);
    }

    function move(step: number) {
        const num = Math.min(Math.max(offset + step, 0), childList.length);
        setOffset(num);
    }

    const showBtn = useMemo(() => totalWidth > width, [width, totalWidth]);

    // 隐藏滚动按钮的同时回到初始位置
    useEffect(() => setOffset(0), [showBtn]);

    return (
        <div
            style={{ width }}
            className={classNames(
                style.root,
                showBtn ? "" : style.noScroll,
                className
            )}>
            {showBtn && (
                <button
                    type="button"
                    className={style.btnPrev}
                    onClick={() => move(-1)}
                    disabled={offset === 0}
                />
            )}

            <div
                ref={ref}
                className={style.wrapper}
                style={{ left: 0 - offsetWidth }}>
                {children}
            </div>

            {showBtn && (
                <button
                    type="button"
                    className={style.btnNext}
                    onClick={() => move(1)}
                    disabled={
                        offsetWidth + width - (showBtn ? 40 : 0) >= totalWidth
                    }
                />
            )}
        </div>
    );
}
